import { __rest, __assign, __spreadArrays } from '../../../_virtual/_tslib.js';
import React from 'react';
import classnames from 'classnames';
import camelCase from 'lodash/camelCase';
import flatten from 'lodash/flatten';
import { parseTemplate, doesTemplateIncludeArea } from './templates.js';
import mapValues from 'lodash/mapValues';
import { SORTED_BREAKPOINT_NAMES } from './breakpoints.js';
import componentConstructorMap from './componentConstructorMap.js';
import styles from '../GridItem/styles.scss.js';
import GridItem from '../GridItem/index.js';
import upperFirst from 'lodash/upperFirst';
import uniq from 'lodash/uniq';

function nextBreakpointWithArea(templates, breakpointName, areaName) {
  var templateBreakpointNames = Object.keys(templates);

  var sortedTemplateBreakpointNames = __spreadArrays(['default'], SORTED_BREAKPOINT_NAMES).filter(function (breakpointName) {
    return templateBreakpointNames.includes(breakpointName);
  });

  var largerTemplateBreakpointNames = sortedTemplateBreakpointNames.slice(sortedTemplateBreakpointNames.indexOf(breakpointName || 'default'));
  return largerTemplateBreakpointNames.find(function (templateBreakpointName) {
    return doesTemplateIncludeArea(templates[templateBreakpointName], areaName);
  });
}

function parseTemplatesToComponents(gridTemplates, gridHash) {
  //  For each grid, we memoize the generated AreaComponentMap so they are re-used across renders.
  //  This has performance benefits, but more importantly it prevents the Grid's children from
  //  losing state across renders (without memoization the AreaComponents would be distinct
  //  constructors with each render)
  if (componentConstructorMap[gridHash]) {
    return componentConstructorMap[gridHash];
  } // Parse each template into a matrix
  //  { 'default' : [['a', 'b'], ['a, 'b']], 'md' : [['foo', 'bar'], ['foo', 'bar']] }


  var parsedTemplates = mapValues(gridTemplates, parseTemplate); // Flatten the templates since we don't care about rows & columns
  //  { 'default' : ['a', 'b', 'a', 'b'], 'md' : ['foo', 'bar', 'foo', 'bar'] }

  var flattenedTemplates = mapValues(parsedTemplates, flatten); // Find all the unique area name tokens across all the grid templates

  var uniqueAreaNames = uniq(flatten(Object.values(flattenedTemplates))); // Build an object mapping raw area names to React components with the `grid-area` preset

  var componentsMap = uniqueAreaNames.reduce(function (components, areaName) {
    var displayName = upperFirst(camelCase(areaName));

    var Component = function Component(_a) {
      var style = _a.style,
          className = _a.className,
          rest = __rest(_a, ["style", "className"]);

      var responsiveClassNames = []; // If this area name isn't used in a given area template, add classes to hide it until
      //   the device reaches the next breakpoint that has a template with that area name.

      for (var _i = 0, _b = Object.entries(flattenedTemplates); _i < _b.length; _i++) {
        var _c = _b[_i],
            breakpointName = _c[0],
            templateAreas = _c[1];

        if (!templateAreas.includes(areaName)) {
          responsiveClassNames.push(styles[breakpointName === 'default' ? 'hide' : breakpointName + "-hide"]);
          var nextBreakpointName = nextBreakpointWithArea(gridTemplates, breakpointName, areaName);

          if (nextBreakpointName) {
            responsiveClassNames.push(styles[nextBreakpointName === 'default' ? 'show' : nextBreakpointName + "-show"]);
          }
        }
      }

      return React.createElement(GridItem, __assign({
        style: __assign({
          gridArea: areaName
        }, style),
        className: classnames(className, responsiveClassNames)
      }, rest));
    };

    Component.displayName = "GridItem(" + displayName + ")";
    components[displayName] = Component;
    return components;
  }, {}); // Memoize

  componentConstructorMap[gridHash] = componentsMap;
  return componentsMap;
}

export default parseTemplatesToComponents;
//# sourceMappingURL=parseTemplatesToComponents.js.map
